import { useState } from "react"
import { Check, AlertCircle } from "lucide-react"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@shadcn/components/ui/input-group"
import { Spinner } from "@shadcn/components/ui/spinner"

export type AutoSaveStatus = "idle" | "saving" | "saved" | "error"

export interface AutoSaveInputProps extends Omit<React.ComponentProps<typeof InputGroupInput>, "onBlur" | "onChange"> {
	/** Current value */
	value: string
	/** Called when value changes */
	onChange: (value: string) => void
	/** Called to save the value when input loses focus. Should return a promise that resolves on success. */
	onSave: (value: string) => Promise<void>
	/** Optional validation function. Return true if valid, false if invalid. */
	validate?: (value: string) => boolean
	/** Current save status */
	status?: AutoSaveStatus
	/** Callback when status changes */
	onStatusChange?: (status: AutoSaveStatus) => void
}

/**
 * Input component with auto-save on blur functionality
 * Shows loading, success, and error indicators
 */
export function AutoSaveInput({
	value,
	onChange,
	onSave,
	validate,
	status: externalStatus,
	onStatusChange,
	...props
}: Readonly<AutoSaveInputProps>) {
	const [internalStatus, setInternalStatus] = useState<AutoSaveStatus>("idle")
	const status = externalStatus ?? internalStatus

	const updateStatus = (newStatus: AutoSaveStatus) => {
		if (onStatusChange) {
			onStatusChange(newStatus)
		} else {
			setInternalStatus(newStatus)
		}
	}

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange(e.target.value)
	}

	const handleBlur = async () => {
		const trimmedValue = value.trim()

		// Skip if value is invalid
		if (validate && !validate(trimmedValue)) {
			return
		}

		updateStatus("saving")
		try {
			await onSave(trimmedValue)
			updateStatus("saved")
			// Auto-hide success indicator after 2 seconds
			setTimeout(() => {
				updateStatus("idle")
			}, 2000)
		} catch (error) {
			updateStatus("error")
			// Auto-hide error indicator after 3 seconds
			setTimeout(() => {
				updateStatus("idle")
			}, 3000)
		}
	}

	return (
		<InputGroup>
			<InputGroupInput value={value} onChange={handleChange} onBlur={handleBlur} {...props} />
			<InputGroupAddon align="inline-end">
				{status === "saving" && <Spinner className="size-4" />}
				{status === "saved" && <Check className="h-4 w-4 text-success-500" />}
				{status === "error" && <AlertCircle className="h-4 w-4 text-destructive" />}
			</InputGroupAddon>
		</InputGroup>
	)
}
